import { useCallback, useRef, useState } from 'react'
import { useGetSet, useIsomorphicLayoutEffect, useMeasure } from 'react-use'

import { prefersReducedMotion } from '../../../../utilities/misc/prefersReducedMotion'
import { range } from '../../../../utilities/misc/range'
import { uniq } from '../../../../utilities/misc/uniq'
import type { Direction, Refs, RefsStates } from './scrollable-types'

export const useRefs = (): Refs => {
  const container = useRef<HTMLDivElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const [outer, { width: outerWidth }] = useMeasure<HTMLElement>()
  const [inner, { width: innerWidth }] = useMeasure<HTMLElement>()

  return {
    ref: { container, wrapper, scroller, outer, inner },
    sizes: { outer: outerWidth, inner: innerWidth },
  }
}

export const useStates = (refs: Refs) => {
  const [getMaxHeight, setMaxHeight] = useGetSet('')
  const [scrollPosition, setScrollPosition] = useState(0)
  const [getTouchPosition, setTouchPosition] = useGetSet({ x: 0, y: 0 })
  const [isClient, setIsClient] = useState(false)

  useIsomorphicLayoutEffect(() => {
    setIsClient(true)

    try {
      const observer = new IntersectionObserver(
        ([entry]) => setMaxHeight(`${entry.intersectionRect.height}`),
        {
          root: null,
          rootMargin: '0px',
          threshold: range(0, 100, 1).map((n) => n / 100),
        }
      )
      if (refs.ref.container.current) {
        observer.observe(refs.ref.container.current)
      }
    } catch (error) {
      // do nothing
    }
  }, [])

  return {
    isClient: { value: isClient },
    maxHeight: { value: getMaxHeight() },
    scrollPosition: { value: scrollPosition, set: setScrollPosition },
    touchPosition: { get: getTouchPosition, set: setTouchPosition },
  }
}

export const useCallbacks = ({ refs, states }: RefsStates) => {
  const onWheel = useCallback(
    ({ deltaX, deltaY }) => {
      document.body.style.overscrollBehavior = 'none'
      const node = refs.ref.scroller.current
      if (node && Math.abs(deltaX) > Math.abs(deltaY)) {
        node.scrollLeft += deltaX
      }
    },
    [refs.ref.scroller]
  )

  const onScroll = useCallback(() => {
    if (refs.ref.scroller.current) {
      states.scrollPosition.set(-refs.ref.scroller.current.scrollLeft)
    }
  }, [refs.ref.scroller, states.scrollPosition])

  const onTouchStart = useCallback(
    ({ touches }) => states.touchPosition.set({ x: touches[0].pageX, y: touches[0].pageY }),
    [states.touchPosition]
  )

  const onTouchMove = useCallback(
    ({ touches }) => {
      const node = refs.ref.scroller.current
      const deltaX = states.touchPosition.get().x - touches[0].pageX
      const deltaY = states.touchPosition.get().y - touches[0].pageY

      if (node && Math.abs(deltaX) > Math.abs(deltaY)) {
        node.scrollLeft += deltaX
      }
      states.touchPosition.set({ x: touches[0].pageX, y: touches[0].pageY })
    },
    [refs.ref.scroller, states.touchPosition]
  )

  const onTouchEnd = useCallback(
    () => states.touchPosition.set({ x: 0, y: 0 }),
    [states.touchPosition]
  )

  const onMouseEnter = useCallback(() => (document.body.style.overscrollBehavior = 'none'), [])

  const onMouseLeave = useCallback(() => (document.body.style.overscrollBehavior = 'auto'), [])

  const onTraverse = useCallback((direction) => handleTraverse({ refs, direction }), [refs])

  const onWrapperScroll = useCallback(
    (event) => states.scrollPosition.set(-event.target.scrollLeft),
    [states.scrollPosition]
  )

  return !isContainmentSupported()
    ? { onTraverse, onWrapperScroll }
    : {
        onWheel,
        onScroll,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
        onMouseEnter,
        onMouseLeave,
        onTraverse,
      }
}

export const isContainmentSupported = (userAgent: string | null = null) => {
  try {
    const isFirefox = (userAgent ?? navigator?.userAgent ?? '').toLowerCase().includes('firefox')
    return isFirefox ? false : CSS.supports('contain', 'content')
  } catch {
    return false
  }
}

export const getScrollState = ({ refs, states }: RefsStates) => {
  try {
    const scrollLeftValue = getScrollLeft(refs)
    const offset = isContainmentSupported() ? 1 : 0
    const scrollLeft = scrollLeftValue ? scrollLeftValue - offset : 0
    const contentWidth = Math.round(refs.sizes.inner - refs.sizes.outer)

    return {
      atStart: Object.is(states.scrollPosition.value, -0) || states.scrollPosition.value >= 0,
      atEnd: scrollLeft >= contentWidth,
      isOverflowing: refs.sizes.outer < refs.sizes.inner,
    }
  } catch {
    return { atStart: true, atEnd: false, isOverflowing: false }
  }
}

export const getScrollLeft = (refs: Refs) =>
  refs.ref.scroller?.current?.scrollLeft ?? refs.ref.wrapper?.current?.scrollLeft ?? 0

export const getInlineStyles = ({
  refs,
  states,
  isOverflowing,
}: RefsStates & { isOverflowing: boolean }) => {
  return {
    ...(isContainmentSupported()
      ? {
          inner: {
            marginLeft: `${isOverflowing ? Math.round(states.scrollPosition.value) : 0}px`,
          },
        }
      : {}),
    scroller: { width: `${refs.sizes.outer}px` },
    handle: { width: `${refs.sizes.inner + 1}px`, border: '1px solid transparent' },
  }
}

export const determineIfButtonsAreHidden = (maxHeight: { value: string }) =>
  parseInt(maxHeight.value, 10) <= 80

export const getColumnPositions = ({
  columnElements,
  offset,
}: {
  columnElements?: NodeListOf<Element>
  offset: number
}) =>
  columnElements
    ? uniq(
        [...columnElements].map((column) =>
          Math.floor(column.getBoundingClientRect().left - offset)
        )
      )
    : []

export const handleTraverse = ({ refs, direction }: { refs: Refs; direction: Direction }) => {
  const tableElement = refs?.ref?.wrapper?.current?.querySelector('table')
  const columnElements = tableElement?.querySelectorAll(
    'td:not(:first-child), th:not(:first-child)'
  )
  const offset = refs?.ref?.wrapper?.current?.parentElement?.getBoundingClientRect().left ?? 0
  const columnOffsets = getColumnPositions({ columnElements, offset })

  const leftPosition = (() => {
    switch (direction) {
      case 'previous':
        return [...columnOffsets].reverse().find((x) => x < 0)
      case 'next':
        return columnOffsets.find((x: number) => x > 0)
    }
  })()

  const scroller = isContainmentSupported() ? refs.ref.scroller : refs.ref.wrapper

  scroller?.current?.scrollTo?.({
    top: 0,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    left: leftPosition !== undefined ? leftPosition + getScrollLeft(refs) : 0,
  })
}
